/**
 * Reconstruction et réconciliation d'un déboursé.
 *
 * Ce module ne lit aucun fichier : il reçoit des lignes déjà extraites du
 * classeur et en fait un arbre chiffré. C'est ce qui permet de le tester sur des
 * cas fabriqués à la main, et de le faire tourner aussi bien dans le navigateur
 * que sur le serveur.
 *
 * **La règle qui gouverne tout ce fichier** (architecture §8.4) : le montant
 * d'une ligne de ressource n'est jamais lu, il est **recalculé**. Sur les 7
 * déboursés réels, la colonne « coût total » du fichier porte le prix unitaire à
 * la place du coût sur 708 lignes sur 2 010 — motif systématique, zéro
 * exception. Les lignes fautives sont individuellement plausibles : rien ne les
 * signalerait.
 */
import { dec, Decimal } from "./nombre.js";
import { totaliser } from "./calcul.js";
/** Ce qu'est un poste dont personne n'a dit d'où il venait : le cas de tout un classeur. */
export const ORIGINE_PAR_DEFAUT = "devis";
/**
 * Le libellé que porte un poste créé, tant que personne ne l'a nommé.
 *
 * Ici et non dans le serveur, parce que l'écran en a besoin **aussi** : c'est lui qui vide le
 * champ à la prise de focus, un marqueur n'étant pas une valeur qu'on corrige au clavier. La
 * même chaîne écrite aux deux endroits finirait par diverger, et le champ cesserait de se
 * vider sans que rien ne le signale.
 */
export const LIBELLE_A_NOMMER = "À nommer";
/**
 * L'unité qui désigne des heures dans le déboursé.
 *
 * Elle est lue dans le fichier, jamais déduite du code de la ressource. C'est
 * une décision de portée : les codes de main-d'œuvre de l'ERP de ROY commencent
 * par `_MOI`, mais ce préfixe est propre à cette installation, et le mettre en
 * dur ferait entrer un client dans le code (README, principe 3).
 *
 * **Mesuré sur les sept déboursés réels** : 1 418 lignes sur 2 010 portent cette
 * unité, dont la totalité des 640 lignes en `_MOI` — aucune exception. Les
 * autres unités rencontrées (U, T, M3, ML, M2, KG, FT, L, J, KM) ne sont pas des
 * durées et n'ont rien à faire dans une somme d'heures.
 */
const UNITE_HEURE = "H";
/**
 * Le type de ressource qui donne la duree d une tache.
 *
 * Declare ici plutot qu ecrit en clair a l endroit du filtre : c est une valeur du
 * referentiel, et la comparer a une chaine litterale perdue dans un calcul rendrait
 * le lien invisible le jour ou le referentiel changerait de vocabulaire.
 */
const TYPE_MAIN_D_OEUVRE = "MO";
/** Cette ligne compte-t-elle des heures ? La question se pose à un seul endroit. */
export function estEnHeures(unite) {
    return unite.trim().toUpperCase() === UNITE_HEURE;
}
/**
 * Cette ligne pèse-t-elle dans une somme d'heures ?
 *
 * En heures, et pas retirée du chiffrage. Les deux conditions ensemble, à un seul endroit :
 * elles étaient écrites trois fois — les heures d'un ouvrage, le besoin par ressource, et
 * depuis le 27/08/2026 la ventilation de la synthèse par famille. Trois filtres jumeaux
 * finiraient par différer d'une condition, et deux tableaux annonceraient alors des heures
 * différentes pour le même chantier sans que rien ne le signale. C'est le raisonnement
 * d'`estMachineImmobilisee`, appliqué au périmètre des heures.
 */
export function compteDesHeures(ressource) {
    return estEnHeures(ressource.unite) && ressource.statut !== STATUT_RETIRE;
}
/**
 * Cette ligne désigne-t-elle une MACHINE, c'est-à-dire quelque chose qui s'immobilise ?
 *
 * En heures, et pas de la main-d'œuvre. Les fournitures en sont exclues : douze regards
 * de visite ne sont pas immobilisés quatre demi-journées, ils sont posés — et leur
 * proposer un nombre d'unités n'aurait aucun sens.
 *
 * Une ligne supprimée n'en est pas une non plus : elle ne compte plus au chiffrage,
 * donc elle n'occupe rien.
 *
 * Écrit ici, et pas dans le calcul ni dans la vue : la proposition d'un nombre
 * d'unités et le coût caché du matériel doivent porter sur **exactement** le même
 * périmètre. Deux prédicats jumeaux finiraient par différer d'une condition, et
 * l'écran proposerait un nombre à une ligne qu'aucun total ne compterait.
 */
export function estMachineImmobilisee(ressource) {
    return (ressource.statut !== STATUT_RETIRE &&
        estEnHeures(ressource.unite) &&
        ressource.typeRessource !== TYPE_MAIN_D_OEUVRE);
}
/**
 * Cette ressource est-elle de la main-d oeuvre comptee en heures ?
 *
 * Ce predicat existe pour que `heuresMO` et `heuresHorsDuree` partagent le MEME
 * perimetre et ne se distinguent que par `porteDuree`. Ecrit deux fois, il finirait
 * par differer d une condition — et les heures ecartees ne se retrouveraient plus
 * dans le total, sans que rien ne le dise.
 */
export function estEnHeuresDeMainDOeuvre(ressource) {
    return estEnHeures(ressource.unite) && ressource.typeRessource === TYPE_MAIN_D_OEUVRE;
}
/** « 2.3.1 » → « 2.3 ». Chaîne vide pour un numéro de premier niveau. */
function numeroParent(numero) {
    const segments = numero.split(".");
    return segments.slice(0, -1).join(".");
}
/**
 * Les nœuds atteignables depuis les racines, en arbre encore en construction.
 *
 * Distinct de `parcourir`, qui travaille sur l'arbre figé : celui-ci doit tourner
 * AVANT `figer`, puisque c'est lui qui vérifie que `figer` peut descendre sans
 * tomber dans un cycle. Il garde ses nœuds vus — un contrôle qui boucle sur la
 * structure qu'il devait déclarer bouclante ne servirait à rien.
 */
function* parcourirEnConstruction(noeuds, vus = new Set()) {
    for (const noeud of noeuds) {
        if (vus.has(noeud))
            continue;
        vus.add(noeud);
        yield noeud;
        yield* parcourirEnConstruction(noeud.enfants, vus);
    }
}
export function construireDepo(lignes) {
    const parNumero = new Map();
    const racines = [];
    const aRattacher = [];
    let courant = null;
    let ressources = 0;
    for (const ligne of lignes) {
        if (ligne.nature === "ressource") {
            if (courant === null) {
                throw new Error(`Ligne ${ligne.ligneTableur} : ressource « ${ligne.code ?? "?"} » rencontrée ` +
                    "avant tout ouvrage. Le fichier ne suit pas la structure attendue.");
            }
            const tempsUnitaire = dec(ligne.quantite);
            const prixUnitaire = dec(ligne.prixUnitaire);
            const coutUnitaire = tempsUnitaire.times(prixUnitaire);
            courant.ressources.push({
                ligneTableur: ligne.ligneTableur,
                // À défaut de rang connu (lecture d'un classeur), c'est la position dans
                // l'ouvrage — la même que celle qu'écrira l'import.
                rang: ligne.rang ?? courant.ressources.length + 1,
                code: ligne.code ?? "",
                // Une ligne lue dans un classeur n'a pas de statut : personne ne l'a
                // encore travaillée, elle est conservée.
                statut: ligne.statut ?? "conserve",
                codeOrigine: ligne.codeOrigine ?? null,
                rangOrigine: ligne.rangOrigine ?? null,
                designation: ligne.designation,
                unite: ligne.unite,
                tempsUnitaire,
                prixUnitaire,
                montantAnnonce: dec(ligne.montantAnnonce),
                coutUnitaire,
                cout: coutUnitaire.times(courant.quantiteVente),
                quantiteReelle: tempsUnitaire.times(courant.quantiteVente),
                typeRessource: ligne.typeRessource ?? null,
                // À défaut d'information (lecture d'un classeur), la ressource porte la
                // durée : c'est le cas de la très grande majorité, et l'exclusion doit être
                // une décision explicite du référentiel, jamais un défaut de lecture.
                porteDuree: ligne.porteDuree ?? true,
                // À défaut d'information, une ressource n'est PAS du transport — l'inverse du
                // défaut de `porteDuree`, et pour la même raison qu'il est à vrai : la valeur
                // par défaut doit être celle qui ne change rien. Faux laisse la ligne porter
                // une barre de durée, ce qu'elle faisait avant que la notion existe.
                estTransport: ligne.estTransport ?? false,
                // Aucun défaut : « on ne sait pas » est une réponse, et c'est celle que l'écran
                // du responsable transport doit compter. La ramener à faux ferait disparaître une
                // machine de sa liste de chargement sans que rien ne le signale.
                porteEngin: ligne.porteEngin ?? null,
                // Absent du référentiel = impacte la barre grise, comme `porteDuree` : c'est le
                // défaut qui ne fait disparaître aucune ressource d'un résumé en silence.
                impacteBarreGrise: ligne.impacteBarreGrise ?? true,
                // Un nombre d'unités absent reste ABSENT : le distinguer de « une » est ce qui
                // permet de n'ajouter aucun coût caché tant que personne n'a rien déclaré.
                unites: ligne.unites ?? null,
                // À la lecture d'un CLASSEUR, rien n'est du groupe : le fichier de l'ERP ne
                // porte pas la provenance. Elle n'apparaît qu'en relisant la base, où le
                // conducteur l'a inscrite.
                duGroupe: ligne.duGroupe ?? false,
                carriereId: ligne.carriereId ?? null,
                carriereCode: ligne.carriereCode ?? null,
                // Même raison : un classeur ne dit pas qui livre. Ces quatre-là ne viennent que de
                // la base, et elles descendront sur la pastille à sa création (041).
                modeLivraison: ligne.modeLivraison ?? null,
                fournisseur: ligne.fournisseur ?? null,
                communeProvenanceId: ligne.communeProvenanceId ?? null,
                villeProvenanceLibre: ligne.villeProvenanceLibre ?? null,
                communeLivraisonId: ligne.communeLivraisonId ?? null,
                villeProvenance: ligne.villeProvenance ?? null,
                codePostalProvenance: ligne.codePostalProvenance ?? null,
                villeLivraison: ligne.villeLivraison ?? null,
                villeLivraisonParDefaut: ligne.villeLivraisonParDefaut ?? false,
                codePostalLivraison: ligne.codePostalLivraison ?? null,
                km: ligne.km ?? null,
                motifSansKm: ligne.motifSansKm ?? null,
                kmEstForce: ligne.kmEstForce ?? false,
                kmDeduit: ligne.kmDeduit ?? null,
            });
            ressources++;
            continue;
        }
        const numero = ligne.numero;
        if (numero === null || numero === "") {
            throw new Error(`Ligne ${ligne.ligneTableur} : ${ligne.nature} sans numéro hiérarchique.`);
        }
        if (parNumero.has(numero)) {
            // Deux postes au même numéro rendraient l'arbre ambigu et le rattachement
            // des suivants dépendrait de l'ordre de lecture.
            throw new Error(`Ligne ${ligne.ligneTableur} : le numéro « ${numero} » apparaît deux fois.`);
        }
        const noeud = {
            chapitre: ligne.nature === "chapitre",
            ligneTableur: ligne.ligneTableur,
            numero,
            // Une ligne lue dans un classeur est retenue : le fichier est le
            // chiffrage de départ, écarter un poste est une décision qui vient après.
            retenu: ligne.retenu ?? true,
            // Absent = du classeur. C'est le cas de toutes les lignes d'un fichier — tout ce
            // qu'un classeur contient en vient — et celui de tout ce qui était en base avant la
            // migration 048.
            origine: ligne.origine ?? ORIGINE_PAR_DEFAUT,
            designation: ligne.designation,
            montantAnnonce: dec(ligne.montantAnnonce),
            unite: ligne.unite,
            quantiteVente: dec(ligne.quantite),
            coutUnitaireAnnonce: dec(ligne.prixUnitaire),
            prixVente: dec(ligne.prixVente ?? "0"),
            // Absent d'un classeur, présent quand la ligne vient de la base. `null` ne
            // signifie pas « pas de groupe » mais « hérite » : c'est `proposerGroupes`
            // qui résout, pas la construction de l'arbre.
            groupeId: ligne.groupeId ?? null,
            // Absent d'un classeur, et absent tant que le conducteur ne s'est pas prononcé.
            personnes: ligne.personnes ?? null,
            ressources: [],
            enfants: [],
        };
        parNumero.set(numero, noeud);
        courant = ligne.nature === "ouvrage" ? noeud : null;
        // Le rattachement est mis en file et résolu APRÈS la lecture complète, alors
        // qu'il se faisait au fil de l'eau. La raison tient au déplacement de blocs
        // (23/09/2026) : rattacher au fil de l'eau exige que le parent ait été lu avant
        // son enfant, ce que la numérotation garantissait — « 3.1 » précède toujours
        // « 3.1.4 » — et qu'un rattachement stocké ne garantit plus, un bloc déplacé
        // pouvant précéder son nouveau parent dans le parcours. Résolu au fil de l'eau,
        // ce bloc-là serait devenu une RACINE en silence, donc un second chantier dans
        // les totaux au lieu d'un bloc rangé sous son chapitre.
        aRattacher.push({
            noeud,
            // Le rattachement stocké fait foi dès que la base s'est prononcée. Le repli
            // sur le numéro n'est pas un défaut de confort : c'est le seul chemin d'un
            // classeur, qui ne porte pas d'autre structure que sa numérotation.
            parent: ligne.parentNumero === undefined ? numeroParent(numero) : ligne.parentNumero,
        });
    }
    // Deuxième passe : le rattachement, dans l'ordre de lecture.
    //
    // L'ordre de `enfants` est celui du parcours, pas celui des numéros : c'est lui
    // que l'écran et le planning rendent, et c'est donc lui qui porte le déplacement.
    for (const { noeud, parent } of aRattacher) {
        const pere = parent === null || parent === "" ? undefined : parNumero.get(parent);
        if (pere === undefined)
            racines.push(noeud);
        else
            pere.enfants.push(noeud);
    }
    // Tout poste doit être atteignable depuis une racine.
    //
    // Le contrôle naît avec le rattachement stocké : la numérotation rendait un cycle
    // IMPOSSIBLE — « 3.1 » est toujours plus court que « 3.1.4 », donc un poste ne
    // pouvait pas descendre de lui-même. Un parent stocké le permet, et le cycle ne se
    // verrait pas ici : il ferait récurser `figer` jusqu'à épuiser la pile, très loin de
    // sa cause. La route de déplacement refuse déjà de déposer un bloc dans sa propre
    // descendance ; ce contrôle-ci est le second verrou, celui qui tient aussi face à
    // une écriture faite à la main dans la base.
    const atteignables = [...parcourirEnConstruction(racines)].length;
    if (atteignables !== parNumero.size) {
        throw new Error(`Structure incohérente : ${parNumero.size - atteignables} poste(s) ne se rattachent à ` +
            "aucune racine. Un rattachement circulaire a été écrit dans la base.");
    }
    const figes = racines.map(figer);
    let chapitres = 0;
    let ouvrages = 0;
    for (const noeud of parNumero.values()) {
        if (noeud.chapitre)
            chapitres++;
        else
            ouvrages++;
    }
    // Les racines écartées sortent des totaux exactement comme un enfant écarté
    // sort de son chapitre. Il n'y a pas de niveau au-dessus pour le faire ici.
    const comptees = figes.filter((n) => n.retenu);
    const ecartes = [...parcourir(figes)].filter((n) => !n.retenu);
    return {
        racines: figes,
        chapitres,
        ouvrages,
        ressources,
        deboursRecalcule: totaliser(comptees.map((n) => n.montantRecalcule)),
        deboursAnnonce: totaliser(comptees.map((n) => n.montantAnnonce)),
        prixVenteTotal: totaliser(comptees.map((n) => n.prixVente)),
        heures: totaliser(comptees.map((n) => n.heures)),
        heuresMO: totaliser(comptees.map((n) => n.heuresMO)),
        heuresHorsDuree: totaliser(comptees.map((n) => n.heuresHorsDuree)),
        besoins: recenserBesoins(comptees),
        postesEcartes: ecartes.length,
        // Le montant d'un chapitre écarté ne compte déjà plus ses propres enfants
        // écartés : les additionner tous ne compte donc rien deux fois.
        deboursEcarte: totaliser(ecartes.map((n) => n.montantRecalcule)),
        prixVenteEcarte: totaliser(ecartes.map((n) => n.prixVente)),
    };
}
/**
 * Le besoin en heures, ressource par ressource, sur tout le déboursé.
 *
 * Trié par heures décroissantes : la question posée à ce tableau est « qu'est-ce
 * qui pèse le plus », et un tri alphabétique obligerait à la relire en entier
 * pour y répondre.
 */
function recenserBesoins(racines) {
    const parCode = new Map();
    for (const noeud of parcourirRetenus(racines)) {
        if (noeud.nature !== "ouvrage")
            continue;
        for (const ressource of noeud.ressources) {
            // Une ligne retirée ne pèse plus sur le plan de charge non plus : c'est du besoin en
            // heures qu'il s'agit, et ces heures-là ne seront pas faites.
            if (!compteDesHeures(ressource))
                continue;
            const cumul = parCode.get(ressource.code) ?? {
                designation: ressource.designation,
                heures: dec(0),
                cout: dec(0),
                lignes: 0,
            };
            parCode.set(ressource.code, {
                designation: cumul.designation,
                heures: cumul.heures.plus(ressource.quantiteReelle),
                cout: cumul.cout.plus(ressource.cout),
                lignes: cumul.lignes + 1,
            });
        }
    }
    return [...parCode.entries()]
        .map(([code, cumul]) => ({ code, ...cumul }))
        .sort((a, b) => b.heures.comparedTo(a.heures));
}
/**
 * Le statut qui retire une ligne du chiffrage (PRD §4.2).
 *
 * Une ligne supprimée reste dans la contre-étude — elle s'affiche barrée, et on
 * peut la remettre — mais elle **ne compte plus**. C'est la seule des six
 * valeurs de statut qui ait une conséquence sur un calcul : les cinq autres
 * décrivent ce qui a été fait, celle-ci change le résultat.
 */
export const STATUT_RETIRE = "supprime";
function figer(noeud) {
    if (!noeud.chapitre) {
        const comptees = noeud.ressources.filter((r) => r.statut !== STATUT_RETIRE);
        const coutUnitaireRecalcule = totaliser(comptees.map((r) => r.coutUnitaire));
        return {
            nature: "ouvrage",
            ligneTableur: noeud.ligneTableur,
            numero: noeud.numero,
            retenu: noeud.retenu,
            origine: noeud.origine,
            designation: noeud.designation,
            unite: noeud.unite,
            quantiteVente: noeud.quantiteVente,
            coutUnitaireAnnonce: noeud.coutUnitaireAnnonce,
            montantAnnonce: noeud.montantAnnonce,
            prixVente: noeud.prixVente,
            // Non renseigné reste NUL, jamais replié sur 1 : c'est ce qui distingue « le
            // conducteur a décidé une personne » de « personne ne s'est prononcé ». Le
            // calcul traite les deux pareil, l'écran ne doit pas.
            personnes: noeud.personnes,
            ressources: noeud.ressources,
            coutUnitaireRecalcule,
            montantRecalcule: coutUnitaireRecalcule.times(noeud.quantiteVente),
            heures: totaliser(noeud.ressources.filter(compteDesHeures).map((r) => r.quantiteReelle)),
            // La MAIN-D OEUVRE seule : c est elle qui donne la duree (PRD 4.4). Une ligne
            // sans type reconnu n y entre pas — mieux vaut une duree visiblement absente
            // qu une duree fausse que rien ne signale.
            //
            // Et parmi elle, seulement ce qui PORTE la duree : le transport en est exclu,
            // ses heures etant concomitantes au travail et non consecutives. Les deux
            // totaux se partagent exactement les memes lignes, sans recouvrement ni oubli.
            heuresMO: totaliser(comptees
                .filter((r) => estEnHeuresDeMainDOeuvre(r) && r.porteDuree)
                .map((r) => r.quantiteReelle)),
            heuresHorsDuree: totaliser(comptees
                .filter((r) => estEnHeuresDeMainDOeuvre(r) && !r.porteDuree)
                .map((r) => r.quantiteReelle)),
            groupeId: noeud.groupeId,
        };
    }
    const enfants = noeud.enfants.map(figer);
    // Un chapitre vaut la somme de ses enfants DIRECTS — ouvrages et
    // sous-chapitres confondus (architecture §8.4) — et RETENUS. C'est le seul
    // endroit où l'exclusion se propage : le poste écarté garde son montant
    // propre, mais personne au-dessus de lui ne l'additionne plus.
    const comptes = enfants.filter((e) => e.retenu);
    return {
        nature: "chapitre",
        ligneTableur: noeud.ligneTableur,
        numero: noeud.numero,
        retenu: noeud.retenu,
        origine: noeud.origine,
        designation: noeud.designation,
        montantAnnonce: noeud.montantAnnonce,
        enfants,
        montantRecalcule: totaliser(comptes.map((e) => e.montantRecalcule)),
        heures: totaliser(comptes.map((e) => e.heures)),
        // La vente suit exactement le même périmètre que les charges. Les deux
        // grandeurs doivent toujours porter sur les mêmes postes, sinon la marge
        // affichée est celle d'un chantier qui n'existe pas.
        prixVente: totaliser(comptes.map((e) => e.prixVente)),
        heuresMO: totaliser(comptes.map((e) => e.heuresMO)),
        heuresHorsDuree: totaliser(comptes.map((e) => e.heuresHorsDuree)),
        groupeId: noeud.groupeId,
    };
}
/** Parcours qui n'entre pas dans les postes écartés — ni eux, ni leur descendance. */
export function* parcourirRetenus(noeuds) {
    for (const noeud of noeuds) {
        if (!noeud.retenu)
            continue;
        yield noeud;
        if (noeud.nature === "chapitre")
            yield* parcourirRetenus(noeud.enfants);
    }
}
/** Parcours de l'arbre, dans l'ordre du fichier. */
export function* parcourir(noeuds) {
    for (const noeud of noeuds) {
        yield noeud;
        if (noeud.nature === "chapitre")
            yield* parcourir(noeud.enfants);
    }
}
//# sourceMappingURL=depo.js.map